import React from 'react';
import { Spin, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSearchByName } from 'api/location-iq.api';
import { useDebounce } from '@react-hook/debounce';
import { mapAutocompleteLocationToPSLocationWithBB } from 'utils/map-location';

type SearchMode = 'places' | 'countries';

export type LocationSelectorProps = Readonly<{
    value: PSLocation | null;
    onChange: (newLocation: PSLocationWithBB) => void;
    hasError?: boolean;
    placeholder?: string;
    searchMode?: SearchMode;
    getSelectedName?: (location: PSLocation) => string;
}>;

export default function LocationSelector({
    value = null,
    onChange,
    placeholder = '',
    hasError = false,
    searchMode = 'places',
    getSelectedName = getLocationSelectedName,
}: LocationSelectorProps) {
    const [searchQueryDebounced, setSearchQueryDebounced] = useDebounce(
        '',
        300,
    );
    const { locationsByName, isValidating, isLoading } = useSearchByName(
        searchQueryDebounced,
        searchMode,
    );

    const locations = (locationsByName ?? [])
        .map(mapAutocompleteLocationToPSLocationWithBB)
        .filter((loc): loc is PSLocationWithBB => loc !== null);

    const getSelectValue = (): string | number | null => {
        if (!value) {
            return null;
        }
        const valueId = value.id;
        if (valueId === undefined) {
            return getSelectedName(value);
        }
        if (!locations.some((item) => String(item.id) === String(valueId))) {
            return getSelectedName(value);
        }
        return valueId;
    };

    return (
        <Select
            showSearch={true}
            status={hasError ? 'error' : undefined}
            placeholder={isLoading ? 'Loading...' : placeholder}
            notFoundContent={isLoading ? <Spin size="small" /> : null}
            filterOption={false}
            value={getSelectValue()}
            onSearch={(text) => setSearchQueryDebounced(text)}
            onChange={(stringValue) => {
                const fullSelectedValue = locations.find(
                    (item) => String(item.id) === String(stringValue),
                );
                if (fullSelectedValue) {
                    onChange(fullSelectedValue);
                }
            }}
            style={{ width: '100%' }}
            size="large"
            suffixIcon={<SearchOutlined />}
            loading={isValidating}
            optionLabelProp="label"
        >
            {locations.map((location) => (
                <Select.Option
                    key={location.id}
                    value={location.id}
                    label={getSelectedName(location)}
                >
                    {getLocationDropdownName(location)}
                </Select.Option>
            ))}
        </Select>
    );
}

function getLocationDropdownName(location: PSLocation): string {
    if (location.name === location.detail) {
        return location.name;
    }
    return `${location.name} (${location.detail})`;
}

function getLocationSelectedName(location: PSLocation): string {
    return getLocationDropdownName(location);
}
